import { useState, useEffect } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import { useGetContactTimingsQuery, useAddContactTimingsMutation } from '../../app/userApi/userApi';
import Button from '../../components/shared/buttons/button';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import './ContactTimings.css';

const ContactTimings = () => {
  const { data: timingsData, isLoading } = useGetContactTimingsQuery();
  const [addContactTimings, { isLoading: isSaving }] = useAddContactTimingsMutation();

  const [timings, setTimings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTimings, setEditTimings] = useState([]);
  const [newTiming, setNewTiming] = useState({ timing: '', active: true });
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (timingsData?.data) {
      setTimings(timingsData.data);
    }
  }, [timingsData]);

  const handleOpenModal = () => {
    setEditTimings([...timings]);
    setNewTiming({ timing: '', active: true });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditTimings([]);
    setNewTiming({ timing: '', active: true });
    setValidationError('');
  };

  const handleTimingChange = (index, field, value) => {
    const updated = [...editTimings];
    updated[index] = { ...updated[index], [field]: value };
    setEditTimings(updated);
  };

  const handleDeleteTiming = (index) => {
    const updated = editTimings.filter((_, i) => i !== index);
    setEditTimings(updated);
  };

  const handleAddCustom = () => {
    if (!newTiming.timing.trim()) {
      setValidationError('Please enter a timing value');
      return;
    }
    setValidationError('');
    setEditTimings([...editTimings, { timing: newTiming.timing, active: newTiming.active }]);
    setNewTiming({ timing: '', active: true });
  };

  const handleSave = async () => {
    try {
      await addContactTimings({ contact_timings: editTimings }).unwrap();
      handleClose();
    } catch (error) {
      console.error('Failed to save:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="contact-timings-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Contact Timings</h4>
        <Button
          label="Edit Timings"
          icon={<FaEdit />}
          onClick={handleOpenModal}
        />
      </div>

      <div className="timings-list">
        {timings.map((item, index) => (
          <div key={item._id || index} className="timing-item">
            <span className="timing-text">{item.timing}</span>
            <span className={`timing-status ${item.active ? 'active' : 'inactive'}`}>
              {item.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        ))}

        {timings.length === 0 && (
          <div className="no-timings">No contact timings found</div>
        )}
      </div>

      <Modal show={showModal} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Contact Timings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="edit-timings-list">
            {editTimings.map((item, index) => (
              <div key={index} className="edit-timing-row">
                <Form.Control
                  type="text"
                  value={item.timing}
                  onChange={(e) => handleTimingChange(index, 'timing', e.target.value)}
                  placeholder="e.g. Morning(9am-12pm)"
                  className="timing-input"
                />
                <Form.Check
                  type="switch"
                  id={`edit-timing-switch-${index}`}
                  checked={item.active}
                  onChange={(e) => handleTimingChange(index, 'active', e.target.checked)}
                  label={item.active ? 'Active' : 'Inactive'}
                  className="timing-toggle"
                />
                <button 
                  className="action-btn delete" 
                  onClick={() => handleDeleteTiming(index)}
                  type="button"
                >
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>

          <div className="add-custom-section">
            <h6>Add Custom Timing</h6>
            <div className="add-custom-row">
              <Form.Control
                type="text"
                value={newTiming.timing}
                onChange={(e) => {
                  setNewTiming({ ...newTiming, timing: e.target.value });
                  if (validationError) setValidationError('');
                }}
                placeholder="e.g. Evening(5pm-7pm)"
                className={`timing-input ${validationError ? 'is-invalid' : ''}`}
                isInvalid={!!validationError}
              />
              <Form.Check
                type="switch"
                id="new-timing-switch"
                checked={newTiming.active}
                onChange={(e) => setNewTiming({ ...newTiming, active: e.target.checked })}
                label={newTiming.active ? 'Active' : 'Inactive'}
                className="timing-toggle"
              />
              <Button
                icon={<FaPlus />}
                className="borderedBtn py-1 px-2"
                size="sm"
                onClick={handleAddCustom}
              />
            </div>
            {validationError && (
              <div className="validation-error">{validationError}</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button label="Cancel" className="borderedBtn" onClick={handleClose} />
          <Button
            label={isSaving ? 'Saving...' : 'Save Changes'}
            onClick={handleSave}
            disabled={isSaving}
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContactTimings;
